import { isIOSWebWithoutNativeHaptics } from "../utils/runtime_platform.js";
import { DAILY_CHALLENGE_DAYS_COUNT } from "../daily_challenge/daily_challenge_config.js";

let default_haptics_enabled: boolean | null = null;

function defaultHapticsEnabled(): boolean
{
  if ( default_haptics_enabled === null )
    default_haptics_enabled = !isIOSWebWithoutNativeHaptics();
  return default_haptics_enabled;
}

export interface GameStatistics
{
  install_date: number;
  music_enabled: boolean;
  sounds_enabled: boolean;
  haptics_enabled: boolean;
  haptics_strength: number;
  level: number;
  points: number;
  seen_level: number;
  last_menu_level: number;
  rewarded_levels: number[];
  adventure_rewards_collected: number[];
  adventure_intro_scroll_played: boolean;
  adventure_intro_scroll_period: string;
  coins: number;
  hearts: number;
  max_hearts: number;
  next_heart_at: number;
  infinite_hearts_until: number;
  no_ads_until: number;
  heart_refill_price: number;
  fireworks: number;
  multiballs: number;
  big_balls: number;
  infinite_balls: number;
  fireworks_price: number;
  fireworks_pack_qty: number;
  rate_dialog_show_count: number;
  rate_dialog_last_shown_level: number;
  rate_dialog_completed: boolean;
  tutorial_completed: boolean;
  big_ball_tutorial_completed: boolean;
  completed_booster_tutorials: number[];
  completed_feature_tutorials: number[];
  new_goal_pending: boolean;
  daily_challenge_completed_days: number;
  daily_challenge_seen_completed_days: number;
}

const STORAGE_KEY = "smash_royale_statistics";
export const DEFAULT_HEART_REFILL_PRICE = 1500;

export function normalizeHeartRefillPrice( value: unknown ): number
{
  return typeof value === "number" && Number.isFinite( value ) && value > 0
    ? value
    : DEFAULT_HEART_REFILL_PRICE;
}

export class StatisticsStorage
{
  static onUpdateHook: (( partial: Partial<GameStatistics> ) => void) | null = null;

  static load(): GameStatistics
  {
    const defaults: GameStatistics = {
      install_date: Date.now(),
      music_enabled: false,
      sounds_enabled: true,
      haptics_enabled: defaultHapticsEnabled(),
      haptics_strength: 0.5,
      level: 1,
      points: 0,
      seen_level: 1,
      last_menu_level: 1,
      rewarded_levels: [],
      adventure_rewards_collected: [],
      adventure_intro_scroll_played: false,
      adventure_intro_scroll_period: "",
      coins: 0,
      hearts: 3,
      max_hearts: 3,
      next_heart_at: 0,
      infinite_hearts_until: 0,
      no_ads_until: 0,
      heart_refill_price: DEFAULT_HEART_REFILL_PRICE,
      fireworks: 0,
      multiballs: 3,
      big_balls: 3,
      infinite_balls: 3,
      fireworks_price: 1500,
      fireworks_pack_qty: 3,
      rate_dialog_show_count: 0,
      rate_dialog_last_shown_level: 0,
      rate_dialog_completed: false,
      tutorial_completed: false,
      big_ball_tutorial_completed: false,
      completed_booster_tutorials: [],
      completed_feature_tutorials: [],
      new_goal_pending: false,
      daily_challenge_completed_days: 0,
      daily_challenge_seen_completed_days: 0,
    };

    const raw = localStorage.getItem( STORAGE_KEY );
    if ( !raw )
    {
      this.save( defaults );
      // try { void DevToDev.sendEvent('first_launch'); } catch {}
      return defaults;
    }

    try
    {
      const parsed = JSON.parse( raw ) as Partial<GameStatistics>;

      const normalized: GameStatistics = {
        ...defaults,
        ...parsed,
        haptics_enabled: ( parsed as any )?.haptics_enabled ?? ( parsed as any )?.vibration_enabled ?? defaults.haptics_enabled,
      };

      const fixNum = ( v: unknown, d: number ) => ( typeof v === 'number' && Number.isFinite( v ) ) ? v : d;

      normalized.haptics_strength = Math.max( 0, Math.min( 1, fixNum( parsed.haptics_strength, defaults.haptics_strength ) ) );

      normalized.level = fixNum( parsed.level, defaults.level );
      normalized.points = fixNum( parsed.points, defaults.points );
      normalized.seen_level = Math.max( 1, fixNum( parsed.seen_level, defaults.seen_level ) );
      // Existing saves predate this field: default it to the current level so upgrading
      // players don't see the menu cart "jump" through all their already-played levels.
      normalized.last_menu_level = Math.max( 1, fixNum( parsed.last_menu_level, normalized.level ) );
      normalized.install_date = fixNum( parsed.install_date, defaults.install_date );
      normalized.rewarded_levels = Array.isArray( parsed.rewarded_levels ) ? parsed.rewarded_levels : defaults.rewarded_levels;
      normalized.adventure_rewards_collected = Array.isArray( parsed.adventure_rewards_collected ) ? parsed.adventure_rewards_collected : defaults.adventure_rewards_collected;
      normalized.adventure_intro_scroll_played = parsed.adventure_intro_scroll_played === true;
      normalized.adventure_intro_scroll_period = typeof parsed.adventure_intro_scroll_period === "string"
        ? parsed.adventure_intro_scroll_period
        : defaults.adventure_intro_scroll_period;

      normalized.coins = Math.max( 0, fixNum( parsed.coins, defaults.coins ) );
      normalized.hearts = Math.max( 0, fixNum( parsed.hearts, defaults.hearts ) );
      normalized.max_hearts = Math.max( 1, fixNum( parsed.max_hearts, defaults.max_hearts ) );
      normalized.next_heart_at = Math.max( 0, fixNum( parsed.next_heart_at, defaults.next_heart_at ) );
      normalized.infinite_hearts_until = Math.max( 0, fixNum( parsed.infinite_hearts_until, defaults.infinite_hearts_until ) );
      normalized.no_ads_until = Math.max( 0, fixNum( parsed.no_ads_until, defaults.no_ads_until ) );
      normalized.heart_refill_price = normalizeHeartRefillPrice(
        parsed.heart_refill_price ?? ( parsed as any ).hearts_refill_price
      );
      normalized.fireworks = Math.max( 0, fixNum( parsed.fireworks, defaults.fireworks ) );
      normalized.multiballs = Math.max( 0, fixNum( parsed.multiballs, defaults.multiballs ) );
      normalized.big_balls = Math.max( 0, fixNum( parsed.big_balls, defaults.big_balls ) );
      normalized.infinite_balls = Math.max( 0, fixNum( parsed.infinite_balls, defaults.infinite_balls ) );
      normalized.fireworks_price = Math.max( 0, fixNum( parsed.fireworks_price, defaults.fireworks_price ) );
      normalized.fireworks_pack_qty = Math.max( 1, fixNum( parsed.fireworks_pack_qty, defaults.fireworks_pack_qty ) );

      const legacy_rate_dialog_shown = ( parsed as any ).rate_dialog_shown === true;
      normalized.rate_dialog_show_count = Math.max(
        0,
        Math.floor( fixNum(
          parsed.rate_dialog_show_count,
          legacy_rate_dialog_shown ? 1 : defaults.rate_dialog_show_count
        ) )
      );
      normalized.rate_dialog_last_shown_level = Math.max(
        0,
        Math.floor( fixNum(
          parsed.rate_dialog_last_shown_level,
          legacy_rate_dialog_shown ? normalized.level : defaults.rate_dialog_last_shown_level
        ) )
      );
      normalized.rate_dialog_completed = parsed.rate_dialog_completed === true;
      normalized.tutorial_completed = parsed.tutorial_completed === true;
      normalized.big_ball_tutorial_completed = parsed.big_ball_tutorial_completed === true;
      normalized.completed_booster_tutorials = Array.isArray( parsed.completed_booster_tutorials )
        ? [ ...new Set( parsed.completed_booster_tutorials.filter(
          value => Number.isInteger( value ) && value >= 0
        ) ) ]
        : [];
      // Preserve progress from saves created while the booster tutorial was
      // implemented only for Big Ball (CumulativeBoosterID.BigBall === 1).
      if ( normalized.big_ball_tutorial_completed
        && !normalized.completed_booster_tutorials.includes( 1 ) )
      {
        normalized.completed_booster_tutorials.push( 1 );
      }
      normalized.completed_feature_tutorials = Array.isArray( parsed.completed_feature_tutorials )
        ? [ ...new Set( parsed.completed_feature_tutorials.filter(
          value => Number.isInteger( value ) && value >= 0
        ) ) ]
        : [];
      normalized.new_goal_pending = parsed.new_goal_pending === true;
      normalized.daily_challenge_completed_days = Math.max(
        0,
        Math.min( DAILY_CHALLENGE_DAYS_COUNT, Math.floor( fixNum( parsed.daily_challenge_completed_days, defaults.daily_challenge_completed_days ) ) )
      );
      normalized.daily_challenge_seen_completed_days = Math.max(
        0,
        Math.min( DAILY_CHALLENGE_DAYS_COUNT, Math.floor( fixNum(
          parsed.daily_challenge_seen_completed_days,
          normalized.daily_challenge_completed_days
        ) ) )
      );

      const serialized = JSON.stringify( normalized );
      if ( serialized !== raw )
      {
        localStorage.setItem( STORAGE_KEY, serialized );
        this.version++;
      }
      return normalized;
    }
    catch
    {
      this.save( defaults );
      return defaults;
    }
  }

  private static version = 0;

  static getVersion(): number
  {
    return this.version;
  }

  static save( stats: GameStatistics )
  {
    localStorage.setItem( STORAGE_KEY, JSON.stringify( stats ) );
    this.version++;
  }

  static remove(): void
  {
    localStorage.removeItem( STORAGE_KEY );
    this.version++;
  }

  static update( partial: Partial<GameStatistics> )
  {
    const current = this.load();
    const updated = { ...current, ...partial } as GameStatistics;
    this.save( updated );

    StatisticsStorage.onUpdateHook?.( partial );

    // try {
    //   if ( typeof partial.player_level === 'number' && partial.player_level !== current.player_level ) {
    //     void DevToDev.sendEvent(`user_property_set_player_level_${partial.player_level}`);
    //     if ( partial.player_level > (current.player_level || 0) ) {
    //       void DevToDev.sendEvent(`level_up_${partial.player_level}`);
    //       void logPlayerLevelMilestones( current.player_level || 0, partial.player_level );
    //     }
    //   }
    //   if ( typeof partial.language === 'string' && partial.language !== current.language ) {
    //     void DevToDev.sendEvent(`user_property_set_language_${partial.language}`);
    //   }
    //   if ( typeof partial.rounds === 'number' && partial.rounds !== current.rounds ) {
    //     void DevToDev.sendEvent(`user_property_set_rounds_${partial.rounds}`);
    //     if ( partial.rounds > (current.rounds || 0) )
    //     {
    //       void logRoundsPlayedMilestones( current.rounds || 0, partial.rounds );
    //     }
    //   }
    // } catch {}
  }
}
